import { Injectable } from '@angular/core';
import { ExternalMembershipRequest } from 'src/app/core/models/local/ExternalMembershipData';
import { SessionStorageService } from './session-storage.service';

@Injectable({ providedIn: 'root' })
export class ExternalMembershipStorageService {
  private readonly storageKey = 'dashboardExternalMemberships';

  constructor(private sessionStorageService: SessionStorageService) {}

  getAccounts(): ExternalMembershipRequest[] {
    try {
      const saved: unknown = JSON.parse(this.sessionStorageService.getItem(this.storageKey) ?? '[]');
      if (!Array.isArray(saved)) return [];

      const accounts: ExternalMembershipRequest[] = [];
      for (const value of saved) {
        const account = this.normalizeAccount(value);
        if (account) accounts.push(account);
      }
      return accounts;
    } catch {
      return [];
    }
  }

  saveAccount(account: ExternalMembershipRequest): void {
    const normalized = this.normalizeAccount(account);
    if (!normalized) return;

    const accounts = this.getAccounts().filter(saved =>
      saved.membershipCode !== normalized.membershipCode && saved.extMembershipCode !== normalized.extMembershipCode
    );
    accounts.push(normalized);
    this.sessionStorageService.setItem(this.storageKey, JSON.stringify(accounts));
  }

  removeAccount(membershipCode: string): void {
    const code = this.normalizeString(membershipCode);
    if (!code) return;

    const accounts = this.getAccounts().filter(account =>
      account.membershipCode !== code && account.extMembershipCode !== code
    );
    if (accounts.length === 0) {
      this.clear();
    } else {
      this.sessionStorageService.setItem(this.storageKey, JSON.stringify(accounts));
    }
  }

  clear(): void {
    this.sessionStorageService.removeItem(this.storageKey);
  }

  private normalizeAccount(value: unknown): ExternalMembershipRequest | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

    const account = value as Record<string, unknown>;
    const membershipCode = this.normalizeString(account.membershipCode)
      || this.normalizeString(account.extMembershipCode);
    const extMembershipCode = this.normalizeString(account.extMembershipCode) || membershipCode;
    // Older sessions used membershipNo; new saves use the API request fields.
    const extMembershipNo = this.normalizeString(account.extMembershipNo)
      || this.normalizeString(account.membershipNo);
    return membershipCode && extMembershipNo ? { membershipCode, extMembershipCode, extMembershipNo } : null;
  }

  private normalizeString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
